<script>
import {defineComponent} from 'vue'
import {mapState, mapGetters} from 'vuex'
import api from '@/api/api.js'

const TEAM_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#f97316', '#14b8a6', '#8b5cf6']

export default defineComponent({
  name: "GenerateDoubleEliminationGrid",
  props: {
    params: {
      type: Object,
      required: false,
      default: () => ({})
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'generate', 'input'],
  data() {
    return {
      isLoadingGenerate: false,
      lowerBracketStartCount: 8,
      firstRoundPairsCount: 4,
      upperBracketTeams: [],
      lowerBracketTeams: [],
      isLoadingTeams: true
    }
  },
  computed: {
    ...mapState('moduleNewTournament', ['selectedStageId', 'tournament']),
    ...mapGetters('moduleNewTournament', ['selectedStage']),
    selectedCount() {
      return this.lowerBracketTeams.length
    },
    isDoubleElimination() {
      return this.selectedStage?.format_id === 2 || this.selectedStage?.format?.name === 'double'
    }
  },
  watch: {
    value(newVal) {
      // Когда модалка закрывается, сбрасываем состояние загрузки
      if (!newVal) {
        this.isLoadingGenerate = false
      }
    }
  },
  async mounted() {
    if (this.isDoubleElimination) {
      await this.loadTeams()
    } else {
      this.$toast.error('Выберите секцию double elimination')
    }
  },
  methods: {
    async loadTeams() {
      try {
        this.isLoadingTeams = true
        const allTeams = []
        let page = 1
        const perPage = 100

        while (true) {
          const response = await api.teams.fromStage(this.selectedStageId, {
            page,
            per_page: perPage,
            status: 'accepted'
          })

          console.log(`Page ${page} response:`, response?.data)
          const teams = response?.data?.stage_teams || []
          const pagination = response?.data?.pagination || {}
          console.log(`Page ${page} teams count:`, teams.length)
          console.log(`Pagination:`, pagination)

          allTeams.push(...teams)

          const totalPages = Math.ceil(pagination.total / pagination.by)
          if (page >= totalPages) break
          page++
        }
        this.upperBracketTeams = allTeams.map((stageTeam, index) => ({
          id: stageTeam.team.id,
          name: stageTeam.team.name,
          color: TEAM_COLORS[index % TEAM_COLORS.length],
          stage_team_id: stageTeam.id
        }))


        this.isLoadingTeams = false
      } catch (error) {
        console.error('Error loading teams:', error)
        this.$toast.error('Не удалось загрузить команды')
        this.isLoadingTeams = false
      }
    },
    moveToLowerBracket(team) {
      if (this.selectedCount >= this.lowerBracketStartCount) return

      const index = this.upperBracketTeams.findIndex(t => t.id === team.id)
      if (index > -1) {
        this.upperBracketTeams.splice(index, 1)
        this.lowerBracketTeams.push(team)
      }
    },
    moveToUpperBracket(team) {
      const index = this.lowerBracketTeams.findIndex(t => t.id === team.id)
      if (index > -1) {
        this.lowerBracketTeams.splice(index, 1)
        this.upperBracketTeams.push(team)
      }
    },
    closeModal() {
      this.$emit('close')
    },
    generate() {
      this.isLoadingGenerate = true
      console.log("generate")
      this.$emit('generate', {
        lowerBracketStartCount: this.lowerBracketStartCount,
        firstRoundPairsCount: this.firstRoundPairsCount,
        lowerBracketTeams: this.lowerBracketTeams,
        upperBracketTeams: this.upperBracketTeams,
      })
      // Не закрываем модалку сразу - это будет делать родительский компонент
      // после успешного завершения генерации
    }
  }
})
</script>

<template>
  <div class="de-modal">
    <!-- Header -->
    <div class="de-modal__header">
      <div class="de-modal__title">
        Генерация сетки этапа
      </div>
      <button class="de-modal__close" @click="closeModal">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <!-- Content -->
    <div class="de-modal__content">
      <!-- Input Fields -->
      <div class="de-modal__inputs">
        <div class="de-modal__input-group">
          <label class="de-modal__label de-modal__label--highlight">
            Кол-ство команд в верхней сетке
          </label>
          <div class="de-modal__input-wrapper">
            <b-form-input
                v-model="lowerBracketStartCount"
                type="number"
                class="de-modal__input"
            />
            <span class="de-modal__counter">
              Выбрано: {{ selectedCount }} / {{ lowerBracketStartCount }}
            </span>
          </div>
        </div>

        <div class="de-modal__input-group">
          <label class="de-modal__label">
            Кол-во команд в первом раунде нижней сетки
          </label>
          <b-form-input
              v-model="firstRoundPairsCount"
              type="number"
              class="de-modal__input"
          />
        </div>
      </div>

      <!-- Team Pools -->
      <div v-if="isLoadingTeams" class="de-modal__loading">
        <b-spinner variant="danger"/>
        <p>Загрузка команд...</p>
      </div>
      <div v-else class="de-modal__pools">
        <!-- Upper Bracket Pool -->
        <div class="de-modal__pool">
          <div class="de-modal__pool-header">
            <span>Пул команд (Верхняя сетка)</span>
            <span class="de-modal__pool-count">{{ upperBracketTeams.length }}</span>
          </div>
          <div class="de-modal__pool-list">
            <div
                v-for="team in upperBracketTeams"
                :key="team.id"
                class="de-modal__team"
                @click="moveToLowerBracket(team)"
            >
              <span class="de-modal__team-dot" :style="{ backgroundColor: team.color }"></span>
              <span class="de-modal__team-name">{{ team.name }}</span>
            </div>
          </div>
        </div>

        <!-- Lower Bracket Pool -->
        <div class="de-modal__pool">
          <div class="de-modal__pool-header">
            <span>Нижняя сетка</span>
            <span class="de-modal__pool-count de-modal__pool-count--accent">
              {{ lowerBracketTeams.length }}
            </span>
          </div>
          <div class="de-modal__pool-list">
            <template v-if="lowerBracketTeams.length > 0">
              <div
                  v-for="team in lowerBracketTeams"
                  :key="team.id"
                  class="de-modal__team"
                  @click="moveToUpperBracket(team)"
              >
                <span class="de-modal__team-dot" :style="{ backgroundColor: team.color }"></span>
                <span class="de-modal__team-name">{{ team.name }}</span>
              </div>
            </template>
            <div v-else class="de-modal__pool-empty">
              <p>Выберите команды из<br>левого списка для старта<br>в нижней сетке</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="de-modal__footer">
      <b-button variant="outline-danger" @click="closeModal">
        Отмена
      </b-button>
      <b-button variant="danger" @click="generate" :disabled="isLoadingGenerate">
        <b-spinner
            v-if="isLoadingGenerate"
            small
            class="mr-1"
            label="Small Spinner"
        />
        Сгенерировать
      </b-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.de-modal {
  background: #fff;
  border-radius: 12px;
  color: #333;
  max-width: 900px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }

  &__close {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 8px;
    transition: color 0.2s;
    font-size: 18px;

    &:hover {
      color: #1f2937;
    }
  }

  &__content {
    padding: 24px;
  }

  &__inputs {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
  }

  &__input-group {
    flex: 1;
  }

  &__label {
    display: block;
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
    font-weight: 500;

    &--highlight {
      color: #ef4444;
    }
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__input {
    background: #f9fafb !important;
    border: 1px solid #e5e7eb !important;
    color: #1f2937 !important;
    border-radius: 8px;
    padding: 12px 16px;

    &:focus {
      border-color: #ef4444 !important;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
    }
  }

  &__counter {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;
    color: #6b7280;
    white-space: nowrap;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #6b7280;

    p {
      margin-top: 16px;
      font-size: 14px;
    }
  }

  &__pools {
    display: flex;
    gap: 20px;
  }

  &__pool {
    flex: 1;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
  }

  &__pool-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    color: #374151;
    font-weight: 600;
    font-size: 14px;
    background: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
  }

  &__pool-count {
    background: #6b7280;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 13px;
    color: #fff;
    font-weight: 500;

    &--accent {
      background: #ef4444;
    }
  }

  &__pool-list {
    height: 280px;
    overflow-y: auto;
    padding: 8px;
  }

  &__team {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__team-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__team-name {
    font-size: 14px;
    font-weight: 500;
  }

  &__pool-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9ca3af;
    text-align: center;
    padding: 20px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.6;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #e5e7eb;

    .btn-outline-danger {
      border-color: #ef4444;
      color: #ef4444;
      padding: 10px 32px;
      border-radius: 8px;
      font-weight: 500;

      &:hover {
        background: #ef4444;
        color: #fff;
      }
    }

    .btn-danger {
      background: #ef4444;
      border-color: #ef4444;
      padding: 10px 32px;
      border-radius: 8px;
      font-weight: 500;

      &:hover {
        background: #dc2626;
      }
    }
  }
}

.de-modal__footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;

}

.de-modal__pool-list {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
}
</style>